import useSWR, { Fetcher } from "swr";
import useSWRImmutable from "swr/immutable";
import authAxios from "../../helpers/authAxios";

export type Project = {
  _id: string;
  name: string;
};

export type ClientProjects = {
  clientName: string;
  projects: Project[];
};

type ResponseState = {
  isLoading: boolean;
  isError: boolean;
};

type SWRProjectList = ResponseState & {
  projectsList: ClientProjects[] | undefined;
};

type remoteClientProjectType = { client_name: string; projects: Project[] };

export type remoteProjectAssignee = {
  _id: string;
  profile: { _id: string; first_name: string; last_name: string };
  profilepic: string;
};

export type AssigneeType = {
  _id: string;
  profilepic: string;
  name: string;
};
export type TaskTotalType = {
  totalTasks: number;
  totalSubTasks: number;
  totalMembers: number;
  result: string[];
};

type Status =
  | "Backlog"
  | "Open"
  | "InProgress"
  | "DevCompleted"
  | "InReview"
  | "Stagging"
  | "QAStagging"
  | "QAProduction"
  | "Done"
  | "Hold";

export type Priority = "High" | "Medium" | "Low";

type Ticket = {
  _id: string;
  name: string;
  due_date: string;
  status: { _id: string; name: Status };
  assignee: AssigneeType;
  estimation_time: number;
  actual_time?:number;
  actual_due_date?:string;
  log_time: number;
  description: string;
  sprint_tag?:string;
};

export type SubTask = Ticket;

export type TaskType = Ticket & {
  taskId: string;
  description: string;
  priority: Priority;
  start_date: string;
  actual_start_date?:string;
  members: AssigneeType[];
  subTasks: SubTask[];
  projectId: string;
  reference_taskId: string;
  documents: [];
  statusId: string;
  createdAt?: string;
  createdBy?: string |any;
  sprint_tag?:string;
};

export type TaskForm = {
  _id: string;
  name: string;
  status: string;
  priority: string;
  estimation_time: number;
  estimation_time_type?: string;
  actual_time?:number;
  actual_time_type?:string;
  log_time: number;
  start_date: string;
  due_date: string;
  actual_start_date?:string;
  actual_due_date?:string;
  reference_taskId?: string;
  description: string;
  projectId: string;
  members: AssigneeType[];
  subTasks: Ticket[];
  documents?: [];
  member?: string;
  sprint_tag?:string;
};

export type ProjectForm = {
  _id: string;
  project_name: string;
  project_tag: string;
  client: string;
  duration: string;
  duration_type: string;
  due_date: string;
  status: string;
  project_type: string;
  description: string;
  manager: string;
  leader: string;
  techLeader:string;
  contributors: AssigneeType[];
  assignees_visibility: boolean;
  contributor?: string;
  manager_data?: string;
  leader_data?: string;
  techLeader_data?:string;
  estimate_start_date?:string;
  estimate_end_date?:string;
  actual_start_date?:string;
  actual_end_date?:string;
  estimate_time?:string;
  estimate_time_type?:string;
  actual_time_type?:string;
  actual_time?:string;
  sprint_tag?:string;
};

export type TaskHistory={
    _id: string;
    changes: {
      field: string;
      oldValue: any;
      newValue: any;
      _id: string;
    }[];
    updatedBy?: {
      _id: string;
      name: string;
    }[];
    deletedBy?: any[];
    createdBy:any[];
    subTaskIds?: string[];
    sprint_tag:string;
    taskName: string;
    taskId: string;
    createdAt: string;
    updatedAt: string;
    __v?: number;
}


class TaskHistoryById {
  private data: TaskHistory;

  constructor(data: TaskHistory) {
    this.data = data;
  }

  get _id() {
    return this.data._id;
  }

  get changes() {
    return this.data.changes.map((change) => ({
      field: change.field,
      oldValue: this.formatDateValue(change.field, change.oldValue),
      newValue: this.formatDateValue(change.field, change.newValue),
      _id: change._id,
    }));
  }

  get updatedBy() {
    return this.data.updatedBy?.map((user) => ({
      _id: user._id,
      name: user.name,
    })) || [];
  }
  get createdBy(){
    return this.data.createdBy?.map((user) => ({
      _id: user._id,
      name: user.name,
    })) || [];
  }

  get deletedBy() {
    return this.data.deletedBy || [];
  }

  get subTaskIds() {
    return this.data.subTaskIds || [];
  }

  get taskName() {
    return this.data.taskName;
  }

  get sprint_tag(){
    return this.data.sprint_tag;
  }

  get taskId() {
    return this.data.taskId;
  }

  get createdAt() {
    return this.formatDate(this.data.createdAt);
  }

  get updatedAt() {
    return this.formatDate(this.data.updatedAt);
  }

  get version() {
    return this.data.__v || 0;
  }

  private formatDate(date: string | any): string {
    return date?.$date || date || "";
  }

  private formatDateValue(field: string, value: any): any {
    if (["start_date", "due_date"].includes(field) && value?.$date) {
      return value.$date; // Return ISO format date
    }
    return value;
  }
}

class ClientProject {
  data;
  constructor(data: remoteClientProjectType) {
    this.data = data;
  }

  get clientName() {
    return this.data.client_name;
  }

  get projects() {
    return this.data.projects;
  }
}

export class Assignee {
  private data;
  constructor(data: remoteProjectAssignee) {
    this.data = data;
  }

  get name() {
    return `${this.data.profile.first_name} ${this.data.profile.last_name}`;
  }

  get profilepic() {
    return this.data.profilepic;
  }

  get _id() {
    return this.data._id;
  }
}

class SubTsk {
  private data;
  constructor(data: any) {
    this.data = data;
  }

  get _id() {
    return this.data._id;
  }

  get name() {
    return this.data.name;
  }

  get due_date() {
    return this.data.due_date;
  }

  get status() {
    return this.data?.status?.name;
  }

  get assignee() {
    return this.data.assignee;
  }

  get estimation_time() {
    return this.data.estimation_time;
  }

  get log_time() {
    return this.data.log_time;
  }

 get sprint_tag(){
    return this.data.sprint_tag;
  }

}

class Task {
  private data;
  constructor(data: any) {
    this.data = data;
  }

  get taskId() {
    return this.data.taskId;
  }

  get name() {
    return this.data.name;
  }

  get _id() {
    return this.data._id;
  }

  get status() {
    return this.data?.status?.name;
  }

  get priority() {
    return this.data.priority;
  }

  get members() {
    return this.data.members;
  }

  get due_date() {
    return this.data.due_date;
  }

  get start_date() {
    return this.data.start_date;
  }

  get actual_due_date() {
    return this.data.actual_due_date;
  }

  get actual_start_date() {
    return this.data.actual_start_date;
  }

  get subTasks() {
    return this.data.subTasks;
  }

  get statusId() {
    return this.data.status._id;
  }

  get estimation_time() {
    return this.data.estimation_time;
  }

  get actual_time() {
    return this.data.actual_time;
  }

  get log_time() {
    return this.data.log_time;
  }

  get reference_taskId() {
    return this.data.reference_taskId;
  }

  get sprint_tag(){
    return this.data.sprint_tag;
  }

  get projectId() {
    return this.data.projectId;
  }

  get createdBy() {
    return this.data.createdBy.name;
  }

  get createdAt() {
    return this.data.createdAt;
  }

  get description() {
    return this.data.description;
  }
}

const getProjectsList = (url: string): Promise<ClientProjects[]> =>
  authAxios.get(url).then((resp): ClientProjects[] => {
    const clientProjectsArr = resp.data?.data;
    return clientProjectsArr.map(
      (clientProject: remoteClientProjectType) =>
        new ClientProject(clientProject)
    );
  });

const getTaskStatus = (url: string): Promise<{ _id: string; name: string }[]> =>
  authAxios.get(url).then((resp) => resp.data?.data);

const getClientList = (
  url: string
): Promise<{ _id: string; client_name: string }[]> =>
  authAxios.get(url).then((resp) => resp.data?.data);

const getProjectAssignees = (url: string): Promise<AssigneeType[]> =>
  authAxios.get(url).then((resp) => {
    const projectAssignees = resp.data?.data;
    return projectAssignees.map(
      (person: remoteProjectAssignee) => new Assignee(person)
    );
  });

const getProjectFilterAssignees = (url: string): Promise<AssigneeType[]> =>
  authAxios.get(url).then((resp) => {
    const projectAssignees = resp.data?.data;
    return projectAssignees;
  });

const getTaskListTotal = (url: string): Promise<TaskTotalType> =>
  authAxios.get(url).then((resp) => {
    const taskListTotal = resp.data?.data;
    return taskListTotal;
  });

const getProjectDetails = (url: string): Promise<any> =>
  authAxios.get(url).then((resp) => resp.data?.data);

const getAssignees = (url: string): Promise<AssigneeType[]> =>
  authAxios.get(url).then((resp) => {
    const assignees = resp.data?.data;
    return assignees.map(
      (person: remoteProjectAssignee) => new Assignee(person)
    );
  });

const getTaskList = (url: string): Promise<TaskType[]> =>
  authAxios.get(url).then((resp) => {
    const { data } = resp.data;
    return data.map((task: any) => new Task(task));
  });

const getTaskHistoryById=(url:string)=>
  authAxios.get(url).then((resp)=>resp?.data);
    
const getFullTaskListById=(url:string)=>authAxios.get(url).then((resp)=>resp?.data)

export const createTask = (formData: any) =>
  authAxios.post("/tasks", formData).then((resp) => resp.data);

export const updateTask = (taskId: string, formData: TaskForm) =>
  authAxios.put(`/tasks/${taskId}`, formData);

export const updateProject = (projectId: string, formData: ProjectForm) =>
  authAxios.put(`/projects/${projectId}`, formData);

export const updateSubTask = (
  taskId: string,
  formData: any,
  isoverride: string
) =>
  authAxios
    .put(`/tasks/subtasks/${taskId}/${isoverride}`, formData)
    .then((resp) => resp.data);

const getUserProjects = (url: string): Promise<Project[]> =>
  authAxios.get(url).then((resp) => {
    const projects = resp.data?.data;
    return projects.map(
      ({ _id, project_name }: { _id: string; project_name: string }) => ({
        _id,
        project_name,
      })
    );
  });

export const createProject = (formData: any) =>
  authAxios.post("/projects", formData).then((resp) => resp.data);

export const createLogTime = (formData: any) =>
  authAxios.post("/logtime", formData).then((resp) => resp.data);

export const deleteTask = (taskId: string) =>
  authAxios.delete(`/tasks/${taskId}`);

const getTaskEstimationTime = (url: string) =>
  authAxios.get(url).then((resp) => resp.data?.result);

export const useProjectsList = () => {
  const { data, error, mutate } = useSWRImmutable(
    "/tasks/projects/list",
    getProjectsList
  );

  return {
    projectsList: data,
    isProjectLoading: !error && !data,
    isError: error,
    projectListMutate: mutate,
  };
};

export const useTaskListTotal = (projectId: string | undefined) => {
  const { data, error } = useSWRImmutable(
    projectId ? `/tasks/total/${projectId}` : null,
    getTaskListTotal
  );
  return {
    taskListTotal: data,
    isLoading: !error && !data,
    isError: error,
  };
};

export const useTaskStatus = () => {
  const { data, error } = useSWRImmutable("/tasks/status/list", getTaskStatus);

  return {
    taskStatus: data || [],
    isLoading: !error && !data,
    isError: error,
  };
};

export const useClientList = () => {
  const { data, error } = useSWRImmutable("/clients/list", getClientList);

  return {
    clients: data || [],
    isLoading: !error && !data,
    isError: error,
  };
};

export const useProjectAssignees = (projectId: string | undefined) => {
  const { data, error } = useSWRImmutable(
    projectId ? `/tasks/employees/list/${projectId}` : null,
    getProjectAssignees
  );
  return {
    projectAssignees: data,
    isLoading: !error && !data,
    isError: error,
  };
};

export const useProjectFilterAssignees = (projectId: string | undefined) => {
  const { data, error } = useSWRImmutable(
    projectId ? `/tasks/employees/filter/${projectId}` : null,
    getProjectFilterAssignees
  );
  return {
    assigneesList: data,
    isLoading: !error && !data,
    isError: error,
  };
};

export const useProjectDetails = (projectId: string | undefined) => {
  const { data, error, mutate } = useSWRImmutable(
    projectId ? `/projects/${projectId}` : null,
    getProjectDetails
  );
  return {
    projectDetails: data,
    isProjectLoading: !error && !data,
    isError: error,
    isProjectMutate: mutate,
  };
};

export const useAssignees = () => {
  const { data, error } = useSWRImmutable(`/employees`, getAssignees);
  return {
    assignees: data || [],
    isLoading: !error && !data,
    isError: error,
  };
};

export const useTaskList = (
  projectId: string | undefined,
  assignee: string | undefined,
  status: string | undefined,
  sort: string,
  sprintTag:string | undefined,
) => {
  const { data, error, mutate } = useSWR(
    projectId
      ? `/tasks/${projectId}/${assignee ? assignee : "All"}/${
          status ? status : "All"
        }/${sort}/${sprintTag ? sprintTag :"All"}`
      : null,
    getTaskList
  );
  return {
    taskList: data,
    isTaskLoading: !error && !data,
    isTaskListError: error,
    taskMutate: mutate,
  };
};

export const useUserProjects = () => {
  const { data, error } = useSWRImmutable(
    "/logtime/projects/list",
    getUserProjects
  );
  return {
    userProjects: data || [],
    isLoading: !error && !data,
    isError: error,
  };
};

export const useTaskEstimation = (taskId: string, subTaskId: string) => {
  const { data, error } = useSWRImmutable(
    `/logtime/task/estimation/${taskId}/${subTaskId}`,
    getTaskEstimationTime
  );
  return {
    taskEstimation: data,
    isLoading: !error && !data,
    isError: error,
  };
};


export const useTaskHistoryById=(taskId:string)=>{
  const {data,error,isValidating,mutate}=useSWRImmutable(
     taskId ?`/tasks/history/${taskId}` :null,   
     getTaskHistoryById
  );
    return{
    taskHistoryData:data ,
    isLoading:!error && !data && !isValidating,
    isError:error,
    refetch:mutate,
  }
}


export const useFullTaskListByProjectId=(projectId:string | undefined)=>{
  const {data,error}=useSWR(projectId ? `/tasks/fulltasks/${projectId}` :"",getFullTaskListById)
  return{
    taskLists:data,
    isError:error,
    isLoading:!error && !data
  }
}